import React from 'react';

import cn from 'bem-cn';

import { RoutePaths } from '../../constants';
import { pluralize } from '../../utils/pluralize/pluralize';
import { getDaysUntil } from '../../utils/date/date';
import { ScreenSize, useGetScreenSize, useVideoOnHover } from '../../hooks';
import { useParallax } from '../PromptCard/useParallax';
import { Link, Video } from '../UI';

import { ChallengeCardProps } from './ChallengeCard.types';

import './ChallengeCard.scss';

const b = cn('challenge');

// Параметры масштабирования для hover эффекта (как в CourseCard)
const scaleX = 1.03;
const scaleY = 1.03;

export const ChallengeCard: React.FC<ChallengeCardProps> = ({
    challenge,
    className,
    withHover = true,
    alwaysOn = false,
}) => {
    const { id, title, videoData, deadline } = challenge;
    const { url: videoUrl, poster: videoPoster } = videoData;
    const screenSize = useGetScreenSize();

    const { onMouseEnter, onMouseLeave, videoRef } = useVideoOnHover();
    const { panelRef, subpanelRef, isHovered, transformX, transformY } = useParallax(1);

    const daysLeft = getDaysUntil(deadline);

    // Включаем hover эффекты только на десктопе
    const needHover = screenSize !== ScreenSize.Mobile && screenSize !== ScreenSize.Tablet && withHover;

    const hover = `${isHovered ? `scale(${scaleX}, ${scaleY})` : ''}`;
    const parallaxStyle = {
        transform: `perspective(200px) rotateY(${transformX}deg) rotateX(${transformY}deg)`,
    };

    const cardParallaxStyle = needHover ? parallaxStyle : {};

    const backgroundHoverStyle = needHover
        ? ({
            transform: `${hover}`,
        } as React.CSSProperties)
        : {};

    const backgroundStyle = {
        '--bg-image': `url(${videoPoster})`,
        ...backgroundHoverStyle,
    };

    return (
        <Link
            to={`${RoutePaths.Challenge}/${id}`}
            className={b({ 'with-hover': withHover, 'always-on': alwaysOn }, [className])}
            ref={panelRef}
            style={cardParallaxStyle}
            onMouseEnter={videoUrl && !alwaysOn ? onMouseEnter : undefined}
            onMouseLeave={videoUrl && !alwaysOn ? onMouseLeave : undefined}
        >
            <div className={b('background')} ref={subpanelRef} style={backgroundStyle}>
                {videoUrl && (
                    <Video
                        ref={videoRef}
                        src={videoUrl}
                        aria-label={title}
                        className={b('video')}
                        muted
                        loop
                        playsInline
                        autoPlay={alwaysOn}
                    />
                )}
            </div>
            <div className={b('content')}>
                <div className={b('header')}>
                    <div>Челлендж</div>
                </div>
                <h2 className={b('title')}>{title}</h2>
                <div className={b('duration')}>
                    {daysLeft >= 0 ? (
                        <>
                            осталось
                            <br />
                            {pluralize(daysLeft + 1, { one: 'день', few: 'дня', many: 'дней' })}
                        </>
                    ) : (
                        <>
                            приём заявок <br /> завершён
                        </>
                    )}
                </div>
            </div>
        </Link>
    );
};
